import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import { fmtDateTime } from '../../lib/transactions/display';

/**
 * The v1 decommission worklist: customers still billing through v1 who have not moved to v2.
 *
 * These are the accounts that make switching v1 off a customer-facing event rather than an internal one.
 * The list is ordered by what turning it off would interrupt, so the conversations can be had in the order
 * that matters.
 */

interface ActiveSub {
  subscriptionId: string; status: string; plan: string;
  amountCents: number; interval: string; currentPeriodEnd?: string;
  /** Volume-tiered usage billing. Its amount is a per-transaction RATE, not a recurring charge. */
  metered: boolean;
}
interface Row {
  legacyId: string; email: string; cognitoId?: string; stripeCustomerId: string;
  createdAt?: string; subscriptions: ActiveSub[]; monthlyValueCents: number; meteredCount: number;
}
interface Audit {
  generatedAt: string;
  legacyUsers: number;
  activeSubscriptions: number;
  items: Row[];
  totalMonthlyValueCents: number;
  meteredCustomers: number;
}

const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    .format(cents / 100);

export function Unmigrated() {
  const isAdmin = useIsSiteAdmin();
  const [q, setQ] = useState('');
  const audit = useQuery({
    queryKey: ['admin-unmigrated'],
    queryFn: () => api.get<Audit>('/admin/unmigrated'),
    enabled: isAdmin,
    // Three whole-collection reads server-side; it is cached there too. Nothing here changes by the minute.
    staleTime: 5 * 60_000,
  });

  const rows = audit.data?.items ?? [];
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.email.toLowerCase().includes(needle) || r.stripeCustomerId.toLowerCase().includes(needle));
  }, [rows, q]);

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>This audit is available to Lakota platform staff only.</p></div>;
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Unmigrated v1 customers</h1>
          <div className="sub">
            Still billing through v1 and not yet on v2 — the accounts that have to move before v1 can be
            switched off.
          </div>
        </div>
      </div>

      {audit.isPending ? <p className="loading-screen">// auditing v1…</p>
        : audit.isError ? <div className="panel empty"><h3>Could not run the audit</h3></div>
        : (
          <>
            <div className="panel unmig-summary">
              <div className="cust-fact">
                <span className="cust-fact-num">{shown.length}</span>
                <span className="cust-fact-lbl">Still on v1, paying</span>
              </div>
              <div className="cust-fact">
                <span className="cust-fact-num">{money(audit.data.totalMonthlyValueCents)}</span>
                {/* Recurring plans only. A metered plan's figure is a per-transaction rate, and adding it
                    to a monthly fee would produce a number that is not any quantity at all. */}
                <span className="cust-fact-lbl">Recurring / month (fixed plans)</span>
              </div>
              <div className="cust-fact">
                <span className="cust-fact-num">{audit.data.meteredCustomers}</span>
                <span className="cust-fact-lbl">On pay-per-transaction</span>
              </div>
              <div className="cust-fact">
                <span className="cust-fact-num">{audit.data.legacyUsers}</span>
                <span className="cust-fact-lbl">v1 users in total</span>
              </div>
              <div className="cust-fact">
                <span className="cust-fact-num">{audit.data.activeSubscriptions}</span>
                <span className="cust-fact-lbl">Active subs (v1 + v2)</span>
              </div>
            </div>

            <div className="spec-toolbar" style={{ margin: '12px 0' }}>
              <input
                className="spec-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Filter by email or Stripe customer…" aria-label="Filter customers"
              />
              <span className="field-help">audited {fmtDateTime(audit.data.generatedAt)}</span>
            </div>

            {rows.length === 0 ? (
              <div className="panel empty">
                <h3>Nobody is left on v1</h3>
                <p>Every v1 customer with an active subscription has moved to v2.</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="panel empty"><h3>No match</h3><p>Nothing matches “{q}”.</p></div>
            ) : (
              <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="tx-table">
                  <thead>
                    <tr>
                      <th>Customer</th><th>Active subscriptions</th>
                      <th style={{ textAlign: 'right' }}>Approx. / month</th><th>v1 since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => (
                      <tr key={r.stripeCustomerId}>
                        <td>
                          {r.email}
                          {/* The identifiers staff need to act: the Stripe customer to look up, and the
                              Cognito id the admin migration tool accepts when email alone does not match. */}
                          <div className="acct-code mono">{r.stripeCustomerId}</div>
                          {r.cognitoId && <div className="acct-code mono">{r.cognitoId}</div>}
                        </td>
                        <td>
                          {r.subscriptions.map((s) => (
                            <div key={s.subscriptionId} className="unmig-sub">
                              <span>{s.plan}</span>
                              <span className="field-help">
                                {s.metered
                                  ? `${money(s.amountCents)}/transaction (from)`
                                  : `${money(s.amountCents)}/${s.interval}`}
                                {s.currentPeriodEnd && ` · renews ${fmtDateTime(s.currentPeriodEnd)}`}
                              </span>
                            </div>
                          ))}
                        </td>
                        <td style={{ textAlign: 'right' }} className="mono">
                          {r.monthlyValueCents > 0 ? money(r.monthlyValueCents)
                            : r.meteredCount > 0 ? <span className="field-help">per transaction</span>
                            : '—'}
                        </td>
                        <td className="tx-created">{r.createdAt ? fmtDateTime(r.createdAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="field-help" style={{ marginTop: 12, maxWidth: '80ch' }}>
              “Migrated” means v2 holds their email address, which is the same rule the just-in-time
              migration matches on — so this list cannot disagree with what happens when one of them signs
              in. “Active” comes from Stripe rather than from either database: a v1 subscription record can
              be stale, a live Stripe subscription cannot. The monthly figure covers FIXED plans only —
              most v1 customers are on pay-per-transaction, whose rate depends on volume this audit does not
              read, so they are counted rather than valued.
            </p>
          </>
        )}
    </div>
  );
}
